/**
 * tradingStore — Zustand store for P&L, trades, and fees.
 *
 * Single source of truth for display-ready values across the app.
 * Updated from two sources:
 *   1. WebSocket portfolio_pnl messages (via LiveTicksContext) — primary, real-time
 *   2. React Query portfolio/orders/charges results (via StoreSync) — fallback/init
 *
 * Components import useTradingStore() and read directly — no per-component
 * fallback chains (livePnl?.x ?? portfolio?.x ?? 0).
 */
import { create } from "zustand";
import type { Position, Order, ChargesReport, KiteHolding } from "@/api/client";
import type {
  BnToneEvent,
  IndicesStatusEvent,
  AppStatusEvent,
  ScanMonitorEvent,
  StockTone,
  ScanCompleteEvent,
  SystemAlertEvent,
} from "@/hooks/useLiveTicks";

type TickStatus = "connecting" | "live" | "error" | "closed";

// ── P&L slice ─────────────────────────────────────────────────────────────────

interface PnlState {
  realisedPnl:        number;
  unrealisedPnl:      number;
  netPnl:             number;
  cashBalance:        number;
  totalInvested:      number;
  netValue:           number;
  winsCount:          number;
  lossesCount:        number;
  tradeCount:         number;
  brokerage:          number;
  openPositionCount:  number;   // from Zerodha — gates LTP unrealised updates
  /** True once the first portfolio_pnl socket message has arrived */
  socketReady:        boolean;
}

// ── Trades slice ──────────────────────────────────────────────────────────────

interface TradesState {
  positions:    Position[];
  todayOrders:  Order[];
  /** Unix ms of the last successful GET /portfolio/ response. 0 = never synced this session. */
  lastSyncedAt: number;
}

// ── Fees slice ────────────────────────────────────────────────────────────────

interface FeesState {
  charges: ChargesReport | null;
}

// ── Holdings slice ────────────────────────────────────────────────────────────

interface HoldingsState {
  holdings: KiteHolding[];
}

// ── WS / Market data slices ───────────────────────────────────────────────────

interface WsMarketState {
  wsStatus:      TickStatus;
  /** Unix ms of last portfolio_pnl WS message — 0 = never received */
  lastPnlAt:     number;
  /** Unix ms of last WS PnL update — used to prevent REST from overwriting fresh WS data */
  _lastWsPnlTs:  number;
  indicesStatus: IndicesStatusEvent | null;
  appStatus:     AppStatusEvent | null;
  scanMonitor:   ScanMonitorEvent | null;
  bnTone:        BnToneEvent | null;
  stockTones:    Record<string, StockTone>;
  scanComplete:  ScanCompleteEvent | null;
  systemAlerts:  SystemAlertEvent[];
}

// ── Actions ───────────────────────────────────────────────────────────────────

interface TradingActions {
  /** Called by LiveTicksContext when a portfolio_pnl WS message arrives */
  setSocketPnl: (data: {
    realised_pnl:        number;
    unrealised_pnl:      number;
    net_pnl:             number;
    cash_balance:        number;
    wins_count:          number;
    losses_count:        number;
    trade_count:         number;
    open_position_count?: number;
  }) => void;

  /** Called by StoreSync when React Query portfolio data loads */
  syncPortfolio: (data: {
    realisedPnl:        number;
    unrealisedPnl:      number;
    netPnl:             number;
    cashBalance:        number;
    totalInvested:      number;
    netValue:           number;
    brokerage:          number;
    winsCount:          number;
    lossesCount:        number;
    tradeCount:         number;
    openPositionCount:  number;
    positions:          Position[];
  }) => void;

  /** Called by StoreSync when React Query orders data loads */
  syncOrders: (orders: Order[]) => void;

  /** Restore lastSyncedAt from localStorage on app mount */
  setLastSyncedAt: (ts: number) => void;

  /** Called by StoreSync when React Query charges data loads */
  syncCharges: (report: ChargesReport) => void;

  /** Called by StoreSync when React Query holdings data loads */
  syncHoldings: (holdings: KiteHolding[]) => void;

  /** Override unrealised P&L with live tick computation from ltpMap */
  setLtpUnrealised: (value: number) => void;

  /** Update positions list from WS without overwriting financial fields */
  setPositions: (positions: Position[]) => void;

  // ── WS/Market actions ──────────────────────────────────────────────────────
  setWsStatus:       (status: TickStatus) => void;
  setIndicesStatus:  (data: IndicesStatusEvent) => void;
  setAppStatus:      (data: AppStatusEvent) => void;
  setScanMonitor:    (data: ScanMonitorEvent) => void;
  setBnTone:         (data: BnToneEvent) => void;
  setStockTones:     (stocks: Record<string, StockTone>) => void;
  setScanComplete:   (data: ScanCompleteEvent) => void;
  addSystemAlert:    (alert: SystemAlertEvent) => void;
  dismissSystemAlert:(timestamp: number) => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useTradingStore = create<PnlState & TradesState & FeesState & HoldingsState & WsMarketState & TradingActions>()((set, get) => ({
  // P&L
  realisedPnl:   0,
  unrealisedPnl: 0,
  netPnl:        0,
  cashBalance:   0,
  totalInvested: 0,
  netValue:      0,
  winsCount:     0,
  lossesCount:   0,
  tradeCount:    0,
  brokerage:          0,
  openPositionCount:  0,
  socketReady:        false,

  // Trades
  positions:    [],
  todayOrders:  [],
  lastSyncedAt: 0,

  // Fees
  charges: null,

  // Holdings
  holdings: [],

  // WS / Market
  wsStatus:      "connecting",
  lastPnlAt:     0,
  _lastWsPnlTs:  0,
  indicesStatus: null,
  appStatus:     null,
  scanMonitor:   null,
  bnTone:        null,
  stockTones:    {},
  scanComplete:  null,
  systemAlerts:  [],

  // ── Actions ──────────────────────────────────────────────────────────────

  setSocketPnl: (data) => {
    // unrealised_pnl from Zerodha is authoritative — overrides any stale MCT local DB computation
    const zerodhaUnrealised = data.unrealised_pnl ?? 0;
    set({
      realisedPnl:        data.realised_pnl,
      unrealisedPnl:      zerodhaUnrealised,
      netPnl:             data.realised_pnl + zerodhaUnrealised,
      cashBalance:        data.cash_balance,
      winsCount:          data.wins_count,
      lossesCount:        data.losses_count,
      tradeCount:         data.trade_count,
      openPositionCount:  data.open_position_count ?? 0,
      socketReady:        true,
      lastPnlAt:          Date.now(),
      _lastWsPnlTs:       Date.now(),
    });
  },

  syncPortfolio: (data) => {
    const now = Date.now();
    const wsFresh = now - get()._lastWsPnlTs < 30_000;

    set({
      // PnL fields: skip if WS data arrived within the last 30s (fresher)
      ...(wsFresh ? {} : {
        realisedPnl:       data.realisedPnl,
        unrealisedPnl:     data.unrealisedPnl,
        netPnl:            data.netPnl,
        winsCount:         data.winsCount,
        lossesCount:       data.lossesCount,
        tradeCount:        data.tradeCount,
        openPositionCount: data.openPositionCount,
      }),
      // REST-only fields: always update
      cashBalance:       data.cashBalance,
      totalInvested:     data.totalInvested,
      netValue:          data.netValue,
      brokerage:         data.brokerage,
      positions:         data.positions ?? [],
      lastSyncedAt:      now,
    });
  },

  syncOrders: (orders) => {
    const today = new Date();
    const todayOrders = orders.filter((o) => {
      const d = new Date(o.created_at);
      return (
        d.getDate()     === today.getDate() &&
        d.getMonth()    === today.getMonth() &&
        d.getFullYear() === today.getFullYear()
      );
    });
    set({ todayOrders });
  },

  setLastSyncedAt: (ts) => set({ lastSyncedAt: ts }),

  syncCharges: (report) => set({ charges: report }),

  syncHoldings: (holdings) => set({ holdings }),

  setLtpUnrealised: (value) => {
    const { realisedPnl } = get();
    set({
      unrealisedPnl: value,
      netPnl:        realisedPnl + value,
    });
  },

  setPositions: (positions) => {
    set({
      positions,
      openPositionCount: positions.filter(p => p.quantity !== 0).length,
    });
  },

  setWsStatus: (wsStatus) => set({ wsStatus }),

  setIndicesStatus: (indicesStatus) => set({ indicesStatus }),

  setAppStatus: (appStatus) => set({ appStatus }),

  setScanMonitor: (scanMonitor) => set({ scanMonitor }),

  setBnTone: (bnTone) => set({ bnTone }),

  setStockTones: (stockTones) => set({ stockTones }),

  setScanComplete: (scanComplete) => set({ scanComplete }),

  addSystemAlert: (alert) => {
    set(state => {
      // Deduplicate: skip if same message received within 30s
      const isDuplicate = state.systemAlerts.some(
        a => a.message === alert.message && alert.timestamp - a.timestamp < 30_000,
      );
      if (isDuplicate) return state;
      return { systemAlerts: [alert, ...state.systemAlerts.slice(0, 4)] };
    });
    // Auto-dismiss after 20s
    setTimeout(() => {
      useTradingStore.getState().dismissSystemAlert(alert.timestamp);
    }, 20_000);
  },

  dismissSystemAlert: (timestamp) => {
    set(state => ({
      systemAlerts: state.systemAlerts.filter(a => a.timestamp !== timestamp),
    }));
  },
}));
